import { CoreEnums } from '@/utils/domain/constants';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isMeaningfulId = (value) =>
  typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== EMPTY_GUID;

const isMeaningfulDisplay = (value) => {
  if (value == null) return false;
  if (typeof value !== 'string') return Boolean(value);
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Avoid rendering raw empty GUIDs as a display name.
  if (trimmed.toLowerCase() === EMPTY_GUID) return false;
  return true;
};

export const isJsonDataCreatorResource = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('resourceType' in value || 'resourceIdentifier' in value || 'resourceDisplay' in value);

/**
 * Normalizes a raw `creator` value into a JSON data-creator resource object.
 * The value may arrive as an already-parsed object or as a serialized JSON
 * string (e.g. `'{"resourceType":1,"resourceDisplay":"...","resourceIdentifier":"..."}'`).
 * Returns `null` when the value is a plain name/email string or cannot be parsed.
 */
export const parseJsonDataCreator = (value) => {
  if (value == null) return null;

  let resource = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Only attempt JSON parsing when it looks like an object payload; plain
    // names/emails are left untouched so callers fall back to legacy behavior.
    if (!trimmed.startsWith('{')) return null;
    try {
      resource = JSON.parse(trimmed);
    } catch (e) {
      return null;
    }
  }

  return isJsonDataCreatorResource(resource) ? resource : null;
};

/**
 * Resolves a raw `creator` value into the props needed to render a user
 * avatar. When the creator is a resource object it exposes the user id
 * (`resourceIdentifier`) so the avatar can load the actual photo; otherwise
 * it returns just the plain name (legacy behavior).
 *
 * Empty / nil GUIDs are treated as missing ids so callers do not paint
 * broken avatars (same rule as the document manager owner cells).
 */
export const resolveJsonDataCreatorAvatar = (value) => {
  const resource = parseJsonDataCreator(value);
  if (resource) {
    const name = resolveJsonDataCreatorDisplay(resource);
    const rawId =
      typeof resource.resourceIdentifier === 'string' ? resource.resourceIdentifier.trim() : null;

    return {
      name: isMeaningfulDisplay(name) ? name : null,
      id: isMeaningfulId(rawId) ? rawId : undefined,
      isAgent: Number(resource.resourceType) === CoreEnums.JsonDataCreatorResourceType.Agent,
    };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return {
      name: isMeaningfulDisplay(trimmed) ? trimmed : null,
      id: undefined,
      isAgent: false,
    };
  }

  return {
    name: null,
    id: undefined,
    isAgent: false,
  };
};

export const resolveJsonDataCreatorDisplay = (resource) => {
  if (resource == null) return null;
  if (typeof resource === 'string') {
    const trimmed = resource.trim();
    return isMeaningfulDisplay(trimmed) ? trimmed : null;
  }

  if (!isJsonDataCreatorResource(resource)) return null;

  const display =
    typeof resource.resourceDisplay === 'string'
      ? resource.resourceDisplay.trim()
      : resource.resourceDisplay;
  if (isMeaningfulDisplay(display)) return display;

  const email =
    typeof resource.resourceExtra?.userEmail === 'string'
      ? resource.resourceExtra.userEmail.trim()
      : resource.resourceExtra?.userEmail;
  if (isMeaningfulDisplay(email)) return email;

  return null;
};
